import React, { useEffect, useState, useCallback } from 'react';
import moment from 'moment';
import loadable from '@loadable/component';
import { Grid, LinearProgress, withTheme } from '@material-ui/core';

import { useHistory, useLocation } from 'react-router';
import sortBy from 'lodash/sortBy';
import AddToCartBoxContainer from '../containers/AddToCartBoxContainer';
import GolfActivityFilters from './GolfActivityFilters';
import useEventListener from '../hooks/useEventListener';
import GolfCourseDetail from './GolfCourseDetail';
import { doSwaggerCall, useSwaggerClient } from '../hooks/useSwaggerClient';
import { ErrorIcon } from '../icons';

const LoadableCourseListSlider = loadable(
    () => import(/* webpackChunkName: "CourseListSlider" */ './CourseListSlider'),
    {
        fallback: <div></div>
    }
);

const LoadableMobileGolfReservationGraph = loadable(
    () =>
        import(
            /* webpackChunkName: "MobileGolfReservationGraph" */ './golfgraph/MobileGolfReservationGraph'
        ),
    {
        fallback: <div></div>
    }
);

const LoadableGolfReservationGraph = loadable(
    () => import(/* webpackChunkName: "GolfReservationGraph" */ './golfgraph/GolfReservationGraph'),
    {
        fallback: <div></div>
    }
);

let cachedTimeslotsResult = false;

function getTimeslotsForActivities(activities, currentFilters, setActivitiesWithTimeslots) {
    const theDate =
        currentFilters.checkInDate === null ? '' : currentFilters.checkInDate.format('YYYY-MM-DD');
    const searchparams = `?fromDate=${theDate}&toDate=${theDate}&groupSize=${
        currentFilters.guests
    }&fromTime=${currentFilters.startTimes.fromTime || ''}&toTime=${currentFilters.startTimes
        .toTime || ''}`;
    const activityids = activities.map(e => e.id).join(',');

    // let's check the cache (this also acts as a debounce)
    if (
        cachedTimeslotsResult &&
        cachedTimeslotsResult.searchparams === searchparams &&
        cachedTimeslotsResult.activityids === activityids
    ) {
        // we have this in cache
        console.log('Cached timeslots hit');
        return setActivitiesWithTimeslots(cachedTimeslotsResult.result);
    }

    let loading = activities.length;
    setActivitiesWithTimeslots({
        data: null,
        error: false,
        loading
    });

    const activitiesWithTimeslots = [];
    return Promise.all(
        activities.map(activity => {
            const req = doSwaggerCall('activities', 'getTimeslotsForActivity', {
                activityId: activity.id,
                fromDate: theDate,
                toDate: theDate,
                groupSize: currentFilters.guests,
                fromTime: currentFilters.startTimes.fromTime,
                toTime: currentFilters.startTimes.toTime
            });
            return req
                .then(({ timeslots }) => {
                    // loading process (except the last one)
                    if (loading > 1) {
                        loading--;
                        setActivitiesWithTimeslots({
                            data: null,
                            error: false,
                            loading
                        });
                    }
                    const extendedActivity = {
                        ...activity,
                        // extend tickets with timeslot information
                        tickets: activity.tickets.map(ticket => ({
                            ...ticket,
                            timeslots: timeslots.filter(t => t.ticket_id === ticket.id),
                            quantity: currentFilters.guests
                        }))
                    };

                    // count all the timeslots, and if there is nothing, simply skip the whole thing
                    if (
                        extendedActivity.tickets.reduce(
                            (sumTimeslots, ticket) => sumTimeslots + ticket.timeslots.length,
                            0
                        ) > 0
                    ) {
                        activitiesWithTimeslots.push(extendedActivity);
                    }
                })
                .catch(err => {
                    console.log(err);
                    // if something cannot be loaded, we just log it but nothing else
                    if (loading > 1) {
                        loading--;
                        setActivitiesWithTimeslots({
                            data: null,
                            error: false,
                            loading
                        });
                    }
                });
        })
    )
        .then(() => {
            const result = {
                data: sortBy(activitiesWithTimeslots, e => e.id),
                error: false,
                loading: 0
            };
            cachedTimeslotsResult = { searchparams, activityids, result };
            // we have this in cache
            return setActivitiesWithTimeslots(result);
        })
        .catch(() => {
            // should never reach this
            setActivitiesWithTimeslots({
                data: null,
                error: true,
                loading: 0
            });
        });
}

function getFilterFromUrl(urlSearchPart) {
    const searchParams = new URLSearchParams(urlSearchPart);
    return {
        checkInDate: moment(searchParams.get('date')).isValid()
            ? moment(searchParams.get('date'))
            : moment(),
        guests: parseInt(searchParams.get('guests'), 10) || 2,
        islands: searchParams.get('island') || window.clientData.defaultIsland || null,
        startTimes:
            searchParams.has('fromTime') && searchParams.has('toTime')
                ? {
                      fromTime: searchParams.get('fromTime'),
                      toTime: searchParams.get('toTime')
                  }
                : {
                      fromTime: '',
                      toTime: ''
                  },
        price:
            searchParams.has('fromPrice') && searchParams.has('toPrice')
                ? {
                      fromPrice: +searchParams.get('fromPrice'),
                      toPrice: +searchParams.get('toPrice')
                  }
                : {
                      fromPrice: 0,
                      // TODO remove hardcoded values
                      toPrice: 200
                  }
    };
}

function getUrlFromFilters(newfilters) {
    // set url based on new filters
    const search = new URLSearchParams();
    if (newfilters.checkInDate) {
        search.append('date', newfilters.checkInDate.format('YYYY-MM-DD'));
    }
    if (newfilters.guests) {
        search.append('guests', newfilters.guests);
    }
    if (newfilters.islands) {
        search.append('island', newfilters.islands);
    }
    if (newfilters.startTimes) {
        search.append('fromTime', newfilters.startTimes.fromTime);
        search.append('toTime', newfilters.startTimes.toTime);
    }
    if (newfilters.price) {
        search.append('fromPrice', newfilters.price.fromPrice);
        search.append('toPrice', newfilters.price.toPrice);
    }
    return search;
}

const GolfReservationPage = ({ theme }) => {
    const history = useHistory();
    const location = useLocation();

    const [selectedActivityId, setSelectedActivityId] = useState(false);

    // FORCE RERENDER ON RESIZE
    const [, updateState] = React.useState();
    useEventListener(window, 'resize', () => updateState({}));

    const [currentFilters, setFilters] = useState(getFilterFromUrl(location.search));
    // on url change, update filters
    useEffect(() => {
        setFilters(getFilterFromUrl(location.search));
    }, [location.search, setFilters]);

    const setCurrentFilters = useCallback(
        newfilters => {
            const search = getUrlFromFilters(newfilters);
            if (search.toString()) {
                history.replace(`?${search.toString()}`);
            }
        },
        [history]
    );

    // When restricted to one island, use default island names directly from client config
    const getGolfIslandParam = () => {
        if (window.clientData?.restrictedToOneIsland) {
            const defaults = window.clientData.defaultIsland;
            return Array.isArray(defaults) && defaults.length > 0
                ? defaults.join(',')
                : undefined;
        }
        return currentFilters.islands;
    };

    const [activities, activitiesLoading, activitiesError] = useSwaggerClient(
        'activities',
        'getAllActivities',
        { island: getGolfIslandParam(), api: 'GolfApi' },
        [],
        data => sortBy(data, e => e.id)
    );
    // selectedActivityId is not in activities list
    useEffect(() => {
        if (activities && !activities.find(e => e.id === selectedActivityId)) {
            setSelectedActivityId(activities.length > 0 ? activities[0].id : false);
        }
    }, [activities, selectedActivityId]);

    const selectedActivity = activities?.find(e => e.id === selectedActivityId);

    const selectActivity = useCallback(
        newActivity => {
            setSelectedActivityId(newActivity.id);
        },
        [setSelectedActivityId]
    );

    const [selectedTimeslot, selectTimeslot] = useState(null);
    const [futureCartItem, setFutureCartItem] = useState(null);
    const [activitiesWithTimeslotsResult, setActivitiesWithTimeslots] = useState({
        data: null,
        error: false,
        // loading here is a count, how many items do we need to load, 0 means nothing
        loading: 0
    });

    const resetBooking = useCallback(() => {
        selectTimeslot(null);
        setFutureCartItem(null);
    }, [selectTimeslot, setFutureCartItem]);

    const selectOtherActivity = useCallback(
        activity => {
            if (selectedActivity && activity && activity.id !== selectedActivity.id) {
                resetBooking();
                selectActivity(activity);
            }
        },
        [resetBooking, selectActivity, selectedActivity]
    );

    const loadActivities = useCallback(() => {
        if (!activities) {
            return;
        }
        resetBooking();
        getTimeslotsForActivities(activities, currentFilters, setActivitiesWithTimeslots);
    }, [resetBooking, activities, setActivitiesWithTimeslots, currentFilters]);

    const notAllFiltersSelected =
        !currentFilters.checkInDate ||
        !currentFilters.guests ||
        !currentFilters.islands ||
        !currentFilters.startTimes ||
        !currentFilters.price;

    // load activities on mount if all filters are selected
    useEffect(() => {
        if (!notAllFiltersSelected) {
            loadActivities();
        }
    }, [notAllFiltersSelected, loadActivities]);

    const noRoundsForDate =
        !activitiesLoading && activities && !activities.length && !notAllFiltersSelected;

    // since we do not push down price filtering to backend (debound) we should do the filtering here
    const availableCourses =
        activitiesWithTimeslotsResult && activitiesWithTimeslotsResult.data
            ? activitiesWithTimeslotsResult.data
                  .filter(({ api }) => api === 'GolfApi')
                  .map(activity => {
                      // we only care about the tickets
                      if (!activity.tickets || activity.tickets.length === 0) {
                          return activity;
                      }
                      // here we can do the price filter
                      const minPrice = currentFilters.price.fromPrice;
                      const maxPrice =
                          currentFilters.price.toPrice >= 200
                              ? 300000
                              : currentFilters.price.toPrice;

                      // also limit to the first ticket (this is shallow copy and should remain one)
                      const firstTicket = {
                          ...activity.tickets[0],
                          timeslots: activity.tickets[0].timeslots.filter(
                              e => e.retail_price > minPrice && e.retail_price < maxPrice
                          )
                      };
                      return {
                          ...activity,
                          tickets: [firstTicket]
                      };
                  })
                  .filter(
                      activity =>
                          activity.tickets &&
                          activity.tickets.length > 0 &&
                          activity.tickets[0].timeslots &&
                          activity.tickets[0].timeslots.length
                  )
            : [];

    const validateTimeslot = (activity, timeslot) => {
        if (timeslot && timeslot.activity.tickets) {
            const guests = [];
            timeslot.activity.tickets.forEach(ticket =>
                guests.push(...Array(ticket.quantity).fill({}))
            );

            // CART ITEM CREATION
            const c = {
                // timeslot based data
                activity_id: timeslot.activity_id,
                start_time: timeslot.start_time,
                end_time: timeslot.end_time,
                warning: timeslot.warning,
                // everything else for cartItem
                addons: [],
                activity,
                tickets: timeslot.activity.tickets.map(ticket => {
                    return {
                        id: ticket.id,
                        name: ticket.name,
                        booking_fields: ticket.booking_fields,
                        guests,
                        quantity: ticket.quantity,
                        retail_price: timeslot.retail_price
                    };
                }),
                agentnote: {}
            };
            setFutureCartItem(c);
        }
        selectTimeslot(null);
    };
    return (
        <Grid className="golf-reservation" container>
            {!!activitiesError && (
                <Grid
                    item
                    container
                    spacing={2}
                    xs={12}
                    style={{
                        color: theme.palette.error.main
                    }}
                    alignItems="center"
                >
                    <Grid item>
                        <ErrorIcon color="error" />
                    </Grid>
                    <Grid item>
                        <span>{activitiesError}</span>
                    </Grid>
                </Grid>
            )}
            <GolfActivityFilters
                filters={currentFilters}
                setFilters={setCurrentFilters}
                notAllFiltersSelected={notAllFiltersSelected}
                loadActivities={() => {
                    loadActivities();
                }}
                loading={activitiesLoading}
            />

            <Grid
                item
                container
                lg={9}
                sm={8}
                xs={12}
                direction="column"
                justifyContent="flex-start"
                className="course-data"
            >
                {!activitiesLoading && (
                    <Grid
                        id="add-to-cart-box"
                        className={`add-to-cart-box ${futureCartItem ? 'is-visible' : ''}`}
                        style={{ marginTop: 0, top: 0 }}
                        item
                    >
                        <AddToCartBoxContainer futureCartItem={futureCartItem} />
                    </Grid>
                )}

                {noRoundsForDate ? (
                    <Grid
                        item
                        container
                        className="mb-3"
                        style={{ paddingTop: '20%' }}
                        justifyContent="center"
                        alignItems="center"
                    >
                        <h1 style={{ textAlign: 'center' }}>
                            No rounds match this filter, please try another one.
                        </h1>
                    </Grid>
                ) : activitiesLoading ? (
                    <Grid item container justifyContent="center" alignItems="center">
                        <div className="search-loading">
                            <div className="mb-2">Loading activities</div>
                            <LinearProgress className="search-bar" />
                        </div>
                    </Grid>
                ) : (
                    !(activities && activities.length > 0) && (
                        <Grid item className="golf-underlay">
                            <h1 style={{ textAlign: 'center', marginTop: '4rem' }}>
                                Find and book your tee time in Hawaii!
                            </h1>
                        </Grid>
                    )
                )}

                {!activitiesLoading && !noRoundsForDate && activities && !!activities.length && (
                    <LoadableCourseListSlider
                        availableCourses={activities}
                        selectedActivityId={selectedActivityId}
                        setSelectedActivityId={setSelectedActivityId}
                    />
                )}

                {activitiesWithTimeslotsResult &&
                    activities &&
                    activities.length > 0 &&
                    activitiesWithTimeslotsResult.loading > 0 && (
                        <Grid item container justifyContent="center" alignItems="center">
                            <div className="search-loading">
                                <div className="mb-2">Loading tee times</div>
                                <LinearProgress
                                    variant="determinate"
                                    className="search-bar"
                                    value={Math.floor(
                                        (100 *
                                            (activities.length -
                                                activitiesWithTimeslotsResult.loading)) /
                                            activities.length
                                    )}
                                />
                            </div>
                        </Grid>
                    )}

                {!activitiesLoading &&
                    !noRoundsForDate &&
                    activities &&
                    !!activities.length &&
                    selectedActivity && (
                        <Grid item id="golf-graph-container">
                            {window.matchMedia('(max-width: 600px)').matches ? (
                                <LoadableMobileGolfReservationGraph
                                    activities={availableCourses}
                                    selectedActivity={selectedActivity}
                                    selectActivity={selectOtherActivity}
                                    selectedTimeslot={selectedTimeslot}
                                    selectTimeslot={selectTimeslot}
                                    validateTimeslot={validateTimeslot}
                                />
                            ) : (
                                <LoadableGolfReservationGraph
                                    activities={availableCourses}
                                    selectedActivityId={selectedActivityId}
                                    selectActivity={selectOtherActivity}
                                    selectedTimeslot={selectedTimeslot}
                                    selectTimeslot={selectTimeslot}
                                    validateTimeslot={validateTimeslot}
                                />
                            )}
                        </Grid>
                    )}

                {!activitiesLoading &&
                    !noRoundsForDate &&
                    activities &&
                    !!activities.length &&
                    selectedActivity && <GolfCourseDetail selectedActivity={selectedActivity} />}
            </Grid>
        </Grid>
    );
};

export default withTheme(GolfReservationPage);
